import { storeToRefs } from "pinia";

import { getLocaleText } from "shared/boot/i18n";
import {
  type UserLocation,
  useUserLocation,
} from "shared/composables/useUserLocation";
import {
  DAY_IN_MILLISECONDS,
  EUROPE,
  EUROPE_EXCLUDED_LOCATION_IDS,
  MEDIA_WITH_ALL_LOCATIONS_BY_PLATFORM,
  NORTH_AMERICA,
  STREAM_EXTENDED_EXPIRY_DAYS,
  streamTypes,
} from "shared/constants";
import {
  getTopLocations,
  shouldIncludeLocation,
} from "shared/helpers/location";
import {
  defaultContentToggles,
  defaultSocialContentToggles,
  getSources,
  type Medium,
  MediumField,
  type MediumName,
} from "shared/helpers/media";
import { type SimpleBoolean } from "shared/resources/Stream";
import { type SimilarMentionsStream } from "shared/services/fetch/fetchSimilarMentions";
import { useGlobalStore } from "shared/stores/global";
import { type OrganisationFeaturesContentToggle } from "shared/stores/organisationFeatures";
import { type CurrentAdminOrganisation } from "shared/stores/user/types";
import {
  type Category,
  type Location,
  type Nullable,
  type Stream,
  type StreamFilter,
} from "shared/types";

export type DefaultOrganisationFeaturesContentToggle = {
  [key in `selected_${MediumField}_content`]?: null;
};

export type MediumFieldKey = `${MediumField}_${number}`;

export interface LegacySimpleBoolean extends SimpleBoolean {
  must__not?: SimpleBoolean["must_not"];
}

export interface StreamQueryFields extends Pick<
  Stream,
  "boolean_query" | "keywords" | "query_style"
> {
  simple_boolean?: Nullable<LegacySimpleBoolean>;
}

// used to test if a stream prop passed to a component is different so we can reload
export function isSameStream(
  newStream: Nullable<Stream>,
  oldStream?: Nullable<Stream>
): boolean {
  return Boolean(oldStream && newStream?.id === oldStream.id);
}

export function isStream(
  stream?: Nullable<Stream | SimilarMentionsStream>
): stream is Stream {
  return Boolean(stream && !("stream_id" in stream));
}

export function isSocialStreamType(
  streamType?: Nullable<
    (typeof streamTypes)[keyof typeof streamTypes] | string | number
  >
) {
  if (!streamType) {
    return false;
  }

  return Number(streamType) === streamTypes.socialStream;
}

export function isQueryPresent(stream: StreamQueryFields): boolean {
  if (stream.query_style === "simple_boolean") {
    const simpleBoolean = stream.simple_boolean;

    if (!simpleBoolean) {
      return false;
    }

    const mustNotClause = simpleBoolean.must_not ?? simpleBoolean.must__not;

    return Boolean(
      simpleBoolean.must?.length ||
      mustNotClause?.length ||
      simpleBoolean.should?.length
    );
  }

  if (stream.query_style === "boolean") {
    return Boolean(stream.boolean_query?.trim());
  }

  return Boolean(stream.keywords?.trim());
}

export function isSocialStream(
  stream?: Nullable<Stream | SimilarMentionsStream>
): stream is Stream & { type: typeof streamTypes.socialStream } {
  if (!isStream(stream)) {
    return false;
  }

  return isSocialStreamType(stream.type);
}

export function streamDisplayLabel(
  stream?: Nullable<Stream | SimilarMentionsStream>,
  fallback = ""
): string {
  if (!isStream(stream)) return fallback;

  const { label } = stream;

  return isSocialStream(stream)
    ? `${label} ${getLocaleText("stream.social_suffix")}`
    : label;
}

export function isSocialBookmarkStream(
  stream?: Nullable<Stream | SimilarMentionsStream>
): stream is Stream & { type: typeof streamTypes.bookmarkStream } {
  return Boolean(isStream(stream) && stream.social_bookmarks_stream);
}

export function labelForStreamType(
  type: (typeof streamTypes)[keyof typeof streamTypes] = streamTypes.mentionStream
): string {
  if (!Number.isInteger(Object.values(streamTypes)[type]))
    return getLocaleText(`stream_type_labels.0`);

  return getLocaleText(`stream_type_labels.${type}`);
}

export function getContentFlags(
  stream: Stream
): Record<string, boolean | undefined> {
  return {
    magazine_content: stream.magazine_content,
    online_content: stream.online_content,
    podcast_content: stream.podcast_content,
    print_content: stream.print_content,
    radio_content: stream.radio_content,
    social_content: stream.social_content,
    tv_content: stream.tv_content,
  };
}

export function expiryHasExtended(
  newStream: Stream,
  oldStream: Stream,
  days: number = STREAM_EXTENDED_EXPIRY_DAYS
): boolean {
  return (
    new Date(newStream.expires_at || 0).getTime() -
      new Date(oldStream.expires_at || 0).getTime() ===
    days * DAY_IN_MILLISECONDS
  );
}

export function mentionStreamDefaultMediaTypes(): OrganisationFeaturesContentToggle {
  return defaultContentToggles();
}

export function mentionStreamSelectedMediaTypes(): DefaultOrganisationFeaturesContentToggle {
  return getSources()
    .map(({ field }) => ({ [`selected_${field}_content`]: null }))
    .reduce((acc, source) => ({ ...acc, ...source }), {});
}

export function socialStreamDefaultMediaTypes(): OrganisationFeaturesContentToggle {
  return defaultSocialContentToggles();
}

export function defaultLocationFilters(
  locations: Location[],
  sources: Medium[],
  organisation: Nullable<CurrentAdminOrganisation> = null
): StreamFilter[] {
  const filters: StreamFilter[] = [];

  const { getUserLocation } = useUserLocation(organisation);
  const userLocation = getUserLocation();
  let excludedLocations: Location[] = [];

  const globalStore = useGlobalStore();
  const { appConfig, isStreem } = storeToRefs(globalStore);

  const isAU = isStreem.value;
  const topLocations: Location[] = getTopLocations(locations, userLocation);

  locations.forEach((location) => {
    if (
      location.type === "Global" ||
      // Only include North America in AU environment
      (!isAU && location.name === NORTH_AMERICA.name)
    ) {
      return;
    }

    let includeLocation = shouldIncludeLocation(location, userLocation);

    const isTopLocation = topLocations.some(
      (topLocation) => topLocation.id === location.id
    );

    if (!includeLocation && !isTopLocation) {
      const locationParent = locations.find(
        (parentLocation) => parentLocation.id === location.parent_id
      );

      if (
        locationParent &&
        locationParent.type !== "Global" &&
        shouldIncludeLocation(locationParent, userLocation)
      ) {
        includeLocation = true;
      }
    }

    if (!includeLocation) {
      const includedChildLocations = locations
        .filter((childLocation) => {
          const isChild = childLocation.parent_id === location.id;

          if (location.name === EUROPE.name) {
            return (
              isChild &&
              !EUROPE_EXCLUDED_LOCATION_IDS.includes(
                childLocation.country_code ?? ""
              )
            );
          }

          return isChild;
        })
        .some((childLocation) =>
          shouldIncludeLocation(childLocation, userLocation)
        );

      if (includedChildLocations) {
        includeLocation = true;
      }
    }

    if (!includeLocation) {
      excludedLocations.push(location);
    }
  });

  excludedLocations = excludedLocations.filter((location) => {
    const isTopLocation = topLocations.some(
      (topLocation) => topLocation.id === location.id
    );

    if (isTopLocation) return true;

    const locationParent = locations.find(
      (parentLocation) => parentLocation.id === location.parent_id
    );

    if (!locationParent) return true;

    const isParentExcluded = excludedLocations.some(
      (excludedLocation) => excludedLocation.id === locationParent.id
    );

    return !isParentExcluded;
  });

  const mediaWithAllLocations: string[] = MEDIA_WITH_ALL_LOCATIONS_BY_PLATFORM[
    appConfig.value.platformName
  ] || ["Social"];

  excludedLocations.forEach((location) => {
    sources.forEach(({ field, medium }) => {
      if (mediaWithAllLocations.includes(medium)) return;

      filters.push({
        action: "exclude",
        filter: location,
        name: location.name,
        scope: field,
        target_id: location.id,
        target_type: "Location",
      });
    });
  });

  const topLocationsFiltered = topLocations.every((location) =>
    filters.some((filter) => filter.target_id === location.id)
  );

  // If all top locations available have been filtered out, include the next top location
  if (topLocationsFiltered) {
    return filters.filter(
      (filter) =>
        filter.target_type === "Location" &&
        filter.target_id !== topLocations[0].id
    );
  }

  return filters;
}

export function defaultCategoryFilters(
  categories: Category[],
  sources: Medium[]
): StreamFilter[] {
  const filters: StreamFilter[] = [];

  categories.forEach((category) => {
    if (category.name === "Aggregator") {
      sources.forEach(({ field }) => {
        if (field === "social") return;

        filters.push({
          action: "exclude",
          filter: category,
          name: category.name,
          scope: field,
          target_id: category.id,
          target_type: "Category",
        });
      });
    }
  });

  return filters;
}

export function mentionStreamDefaultMedia() {
  return getSources()
    .map(({ field }) => ({ [`${field}_content`]: true }))
    .reduce((acc, source) => ({ ...acc, ...source }), {});
}

export function socialStreamDefaultMedia() {
  return getSources()
    .map(({ field }) => ({ [`${field}_content`]: field === "social" }))
    .reduce((acc, source) => ({ ...acc, ...source }), {});
}

export function labelForCoverage(type = streamTypes.mentionStream) {
  if (isSocialStreamType(type))
    return getLocaleText("mention_stream_builder_settings.social_posts_label");

  return getLocaleText("mention_stream_builder_settings.coverage_label");
}

export function mediaWithLocationsByPlatform(): MediumName[] {
  const globalStore = useGlobalStore();
  const { appConfig } = storeToRefs(globalStore);

  return (
    MEDIA_WITH_ALL_LOCATIONS_BY_PLATFORM[appConfig.value.platformName] || [
      "Social",
    ]
  );
}

export function defaultTopLocations(
  locations: Location[],
  userLocation: UserLocation
): Location[] {
  const topLocations = getTopLocations(locations, userLocation);

  return topLocations.filter((location) =>
    shouldIncludeLocation(location, userLocation)
  );
}

export function defaultLocationKeys(
  locations: Location[],
  organisation: CurrentAdminOrganisation
): MediumFieldKey[] {
  const sources = getSources();
  const { getUserLocation } = useUserLocation(organisation);
  const userLocation = getUserLocation();
  const topLocations = defaultTopLocations(locations, userLocation);
  const allLocations = mediaWithLocationsByPlatform();

  return sources
    .filter((source) => source.field !== MediumField.SOCIAL)
    .flatMap((source) =>
      allLocations.includes(source.medium)
        ? []
        : topLocations.map(
            (location) => `${source.field}_${location.id}` as MediumFieldKey
          )
    );
}

export function defaultCategoryKeys(categories: Category[]): MediumFieldKey[] {
  const globalStore = useGlobalStore();

  if (globalStore.isCisionOne) {
    return [];
  }

  const sources = getSources();
  const categoryKeys: MediumFieldKey[] = [];

  categories.forEach((category) => {
    if (category.name === "Aggregator") return;

    sources.forEach((medium) => {
      if (medium.field === "social") return;
      categoryKeys.push(`${medium.field}_${category.id}`);
    });
  });

  return categoryKeys;
}
